import { FacilityCountBaselineSource, FacilityCurrentOccupancy, getTargetTypeDisplayName, TargetTypes } from "@app/shared";
import { formatAgo } from "@app/shared/src/components_v2/formatters";
import { formatInTimeZone } from "date-fns-tz";
import { HoverTooltip } from "../../../shared/HoverTooltip";
import { useRef, useState } from "react";

type FacilityOccupancyOverlayProps = {
    occupancy: FacilityCurrentOccupancy | undefined;
    /** The facility's own zone — the baseline's timestamp is only meaningful in local terms. */
    timeZone: string;
    /** Sends the user to the ground truth panel's update action and draws attention to it. */
    onEstablishBaseline: () => void;
};

/** "Cars, Semi Trucks", or undefined when the filter isn't known yet. */
function describeTargetFilter(filter: number | undefined): string | undefined {
    if (filter === undefined) return undefined;
    if (!filter) return "all target types";

    const known = Object.values(TargetTypes).filter((v): v is number => typeof v === "number" && v !== 0);
    const selected = known.filter(v => (filter & v) !== 0);

    // A mask that covers everything says "all target types" rather than listing all of them. This
    // is the common case, not an edge one: GetConfiguredTargetFilter unions the facility's event
    // trigger filters, and a trigger set to every type yields 2147483647 — which spelled out is
    // nineteen names, five lines deep, in a card sized for one.
    if (selected.length === known.length) return "all target types";

    const names = selected.map(v => getTargetTypeDisplayName(v)).filter(Boolean);
    return names.length ? names.join(", ") : "all target types";
}

/**
 * Vehicles on site now, over the map.
 *
 * Always labelled, including when the number is unknown: this is a new panel and nothing else on
 * screen says what it is, so a bare "?" would just be a mystery glyph. The count is only knowable
 * relative to a starting total, so with none set it shows "?" and explains how to fix that.
 *
 * Shows "142 ±1" when the server reports crossings that might sit either side of the baseline
 * instant — see FacilityCurrentOccupancy.CountUncertainty. Normally there are none and the number
 * stands alone; printing a bare figure when it could be off would be false precision on the one
 * number this whole feature exists to make trustworthy.
 */
export const FacilityOccupancyOverlay: React.FC<FacilityOccupancyOverlayProps> = ({ occupancy, timeZone, onEstablishBaseline }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const anchorRef = useRef(null);

    // Renders even with no data — a vanished overlay looks like a broken page, and a missing
    // baseline is the state most worth explaining. Covers first load and a failed fetch alike.
    const { count, baselineCount, baselineAsOfDate, baselineSource, changeSinceBaseline, targetFilter } = occupancy ?? {};
    // Zero on a quiet lot, which is the normal case — shown only when the count really could be off.
    const uncertainty = occupancy?.countUncertainty ?? 0;
    const needsBaseline = occupancy?.needsBaseline ?? true;
    // Undefined rather than "all vehicles" until the server says: claiming no filter would be wrong
    // at a site whose triggers only watch a subset.
    const filterLabel = describeTargetFilter(occupancy ? Number(targetFilter ?? 0) : undefined);

    const card = (
        <div
            ref={anchorRef}
            className="absolute top-3 left-3 z-10 rounded shadow-lg border border-separators-page bg-background-page backdrop-blur-sm px-4 py-3 min-w-[14rem] max-w-[20rem] text-body-text"
            onMouseOver={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <div className="text-xs font-semibold uppercase tracking-wide text-semi-bright-text">
                Vehicles on site
            </div>

            <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-4xl font-bold leading-none mx-auto text-brand-primary">
                    {needsBaseline ? <span aria-label="Not yet known">?</span> : count}
                    {!needsBaseline && uncertainty > 0 &&
                        <span className="text-lg font-semibold align-top ml-0.5" aria-label={`plus or minus ${uncertainty}`}>
                            ±{uncertainty}
                        </span>
                    }
                </span>
                {!needsBaseline &&
                    <span className="text-xs text-semi-bright-text">
                        {(changeSinceBaseline ?? 0) >= 0 ? "+" : "−"}{Math.abs(changeSinceBaseline ?? 0)}
                    </span>
                }
            </div>

            {filterLabel && <div className="mt-1.5 text-xs text-muted-body-text">Counting {filterLabel}</div>}
        </div>
    );

    return (
        <HoverTooltip show={showTooltip} popperButtonRef={anchorRef} hoverable={card}>
            <div className="max-w-xs text-sm p-1">
                {needsBaseline ?
                    <>
                        <p>
                            This shows how many vehicles are on site right now. It's worked out by
                            taking a known total and adding everything that has driven in or out
                            since, so it needs a starting total before it can show a number.
                        </p>
                        <button
                            type="button"
                            onClick={onEstablishBaseline}
                            className="mt-2 text-link-page hover:text-link-page-hover underline font-semibold"
                        >
                            Establish baseline
                        </button>
                    </>
                    :
                    // Absolute time carries the zone, since a bare clock time on a site in another
                    // state is ambiguous; the relative form is what makes staleness obvious.
                    <p>
                        {(changeSinceBaseline ?? 0) >= 0 ? "+" : "−"}{Math.abs(changeSinceBaseline ?? 0)}
                        {" since "}
                        {baselineAsOfDate
                            ? `${formatInTimeZone(baselineAsOfDate, timeZone, "MM/dd/yyyy h:mm aa zzz")} (${formatAgo(baselineAsOfDate)})`
                            : "the baseline"}
                        {baselineSource === FacilityCountBaselineSource.GroundTruth
                            ? `, from a ground truth count of ${baselineCount}.`
                            : `, from an entered count of ${baselineCount}.`}
                        {uncertainty > 0 &&
                            <>
                                {" "}
                                <span className="block mt-2">
                                    {uncertainty === 1 ? "One vehicle was" : `${uncertainty} vehicles were`}
                                    {" still being tracked when the baseline was taken, so "}
                                    {uncertainty === 1 ? "it" : "they"}
                                    {" may already be included in it. The true figure is within "}
                                    ±{uncertainty} of this. Take a fresh count to clear it.
                                </span>
                            </>
                        }
                    </p>
                }
            </div>
        </HoverTooltip>
    );
};
